/**
 * The country codes the domain registrar accepts for a registrant contact —
 * Route 53 Domains' `CountryCode` enum: ISO 3166-1 alpha-2 plus a few legacy
 * codes the registries still honour.
 *
 * `@fias/webapp-client` does not depend on `@fias/db-types`, so this is a copy
 * of `REGISTRAR_COUNTRY_CODES` there (the server's schema is the boundary and
 * refuses anything else). Pinned equal by
 * `tests/architecture/registrar-country-codes-client-parity.test.ts`. This
 * module imports nothing, so that test can load it outside the client's
 * module graph.
 */
export const REGISTRAR_COUNTRY_CODES = [
  'AC',
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AN',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TP',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
] as const;

export type RegistrarCountryCode = (typeof REGISTRAR_COUNTRY_CODES)[number];

const REGISTRAR_COUNTRY_CODE_SET: ReadonlySet<string> = new Set(REGISTRAR_COUNTRY_CODES);

export function isRegistrarCountryCode(value: string): value is RegistrarCountryCode {
  return REGISTRAR_COUNTRY_CODE_SET.has(value);
}

export interface RegistrarCountryOption {
  code: RegistrarCountryCode;
  label: string;
}

/**
 * The registrar's countries named in the viewer's language, sorted by name.
 * A legacy code the browser names like a current one (`AN` → Curaçao, as
 * `CW`) carries its code so the two stay apart; a code the browser cannot
 * name shows as the code.
 */
export function registrarCountryOptions(locale?: string): RegistrarCountryOption[] {
  const names = regionNames(locale);
  const labelled = REGISTRAR_COUNTRY_CODES.map((code) => ({ code, label: names(code) }));
  const seen = new Map<string, number>();
  for (const o of labelled) seen.set(o.label, (seen.get(o.label) ?? 0) + 1);
  return labelled
    .map((o) => ((seen.get(o.label) ?? 0) > 1 ? { ...o, label: `${o.label} (${o.code})` } : o))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

function regionNames(locale?: string): (code: string) => string {
  try {
    const names = new Intl.DisplayNames(locale ? [locale] : undefined, {
      type: 'region',
      fallback: 'code',
    });
    return (code) => names.of(code) ?? code;
  } catch {
    return (code) => code;
  }
}
